import { AccountType, InvoicingData } from "./UserTypes";
import { BaseWallet, Chain } from "./WalletTypes";
import { PaginatedQueryParams } from "./GlobalTypes";

export enum InvoiceUserType {
  PAYER = "payer",
  ISSUER = "issuer",
}

export enum InvoiceStatus {
  DRAFT = "DRAFT",
  PENDING = "PENDING",
  PROCESSING = "PROCESSING",
  PAID = "PAID",
  CANCELLED = "CANCELLED",
  PENDING_REVIEW = "PENDING_REVIEW",
}

export enum CurrencyType {
  FIAT = "fiat",
  CRYPTO = "crypto",
}

export enum Currency {
  EUR = "eur",
  GBP = "gbp",
  USD = "usd",
  USDT = "usdt",
  USDC = "usdc",
  INR = "inr",
  PHP = "php",
  PKR = "pkr",
}

interface BaseCurrencyDetails {
  name: string;
  symbol: string;
  decimals: number;
}

export interface CurrencyDetails extends BaseCurrencyDetails {
  type: CurrencyType;
  value: Currency;
  code: string;
  alternativeSymbol?: string;
  features: Array<keyof IncomingPaymentDetails>;
  icon?: string;
  supportedChains?: Array<Chain>;
}

export type Job = {
  description: string;
  rate: number;
  incoming_rate?: number;
};

export interface Party extends InvoicingData {
  account_type?: AccountType;
  name?: string;
  email?: string;
}

export interface IncomingPaymentDetails {
  beneficiary_name?: string;
  iban?: string;
  account_number?: string;
  reference?: string;
  bank_name?: string;
  bic?: string;
  bank_code?: string;
  bank_code_sort_code?: string;
  bank_code_ach?: string;
  bank_code_fed?: string;
  crypto_chain?: string;
  crypto_address?: string;
  beneficiary_address?: string;
  bank_address?: string;
}

export interface OutgoingPaymentDetails
  extends Omit<BaseWallet, "address" | "id"> {
  wallet_id: string;
  address?: string;
}

export interface TransactionDetails {
  amount: number;
  paid_at: string;
  commission_percent: string;
  commission_amount: number;
  outgoing_amount: number;
  outgoing_chain: Chain;
  outgoing_currency: Currency;
  explorer_link: string;
  price: number;
}

interface BaseInvoiceResponse {
  id: string;
  public_id?: string;
  number?: string;
  status: InvoiceStatus;
  currency?: Currency;
  outgoing_currency?: Currency;
  amount: number;
  created_at?: string;
  updated_at?: string;
  issued_at?: string;
  due_at?: string;
  user_is_creator?: boolean;
  items_amounts_are_in_outgoing_currency?: boolean;
  transaction_details?: TransactionDetails;
}

export interface Invoice extends BaseInvoiceResponse {
  include_vat?: boolean;
  taxed_at?: string;
  note?: string;
  issuer_data?: Party;
  payer_data?: Party;
  items?: Job[];
  outgoing_payment_details?: OutgoingPaymentDetails;
  incoming_payment_details?: IncomingPaymentDetails;
  user_is_creator?: boolean;
  user_is_issuer?: boolean;
  user_is_payer?: boolean;
  approved_by_creator?: boolean;
  declined_reason?: string;
  issuer_id?: string;
  payer_id?: string;
  show_transaction_data_on_public_view?: boolean;
  max_amount_threshold?: number;
  min_amount_threshold?: number;
}

export interface InvoiceShort extends BaseInvoiceResponse {
  is_public: boolean;
  payer_name?: string;
  issuer_name?: string;
}

export interface CreateInvoicePayload {
  issued_at?: string;
  due_at?: string;
  issuer_id?: string;
  payer_id?: string;
  issuer_data?: Party;
}

export interface UpdateInvoicePayload {
  id: string;
  number?: string;
  issued_at?: string;
  due_at?: string;
  issuer_data?: Party;
  payer_data?: Party;
  currency?: Currency;
  outgoing_currency?: Currency;
  items?: Job[];
  note?: string;
  outgoing_payment_details?: {
    wallet_id: string;
  };
  items_amounts_are_in_outgoing_currency?: boolean;
}

export interface InvoiceListFilter {
  status: string | undefined;
}

export interface InvoiceQueryParams
  extends PaginatedQueryParams,
    InvoiceListFilter {
  user_is?: InvoiceUserType;
}

export type InvoiceCounts = Record<string, number>;

export interface DeclineInvoicePayload {
  id: string;
  declined_reason: string;
}

export type InvoiceCurrencies = {
  incoming: CurrencyDetails | undefined;
  outgoing: CurrencyDetails;
  input: CurrencyDetails | undefined;
};
